import { getValidAuthorizationHeader } from '../auth/cognito';
import { getReferralsApiUrl } from '../config/api';
const LOG_PREFIX = '[workflow]';
const FORM_CACHE_TTL_MS = 15 * 60 * 1000;
const FORM_DEFINITION_CACHE_KEY = 'wc_portal_form_definition_cache_v2';
let formDefinitionCache = {};
function isFormDefinitionCacheEntryValid(entry) {
    return (!!entry?.detail?.survey &&
        typeof entry.fetchedAt === 'number' &&
        Date.now() - entry.fetchedAt < FORM_CACHE_TTL_MS);
}
function readPersistedFormCache() {
    try {
        const stored = window.localStorage.getItem(FORM_DEFINITION_CACHE_KEY);
        if (!stored)
            return {};
        const parsed = JSON.parse(stored);
        if (!parsed || typeof parsed !== 'object')
            return {};
        return parsed;
    }
    catch {
        return {};
    }
}
function writePersistedFormCache(cache) {
    try {
        window.localStorage.setItem(FORM_DEFINITION_CACHE_KEY, JSON.stringify(cache));
    }
    catch (err) {
        logWorkflow('unable to persist form cache', err);
    }
}
function getCachedFormDefinition(formId) {
    const memory = formDefinitionCache[formId];
    if (isFormDefinitionCacheEntryValid(memory)) {
        logWorkflow('form cache hit', {
            formId,
            source: 'memory',
            ageMs: Date.now() - memory.fetchedAt,
        });
        return memory.detail;
    }
    const persisted = readPersistedFormCache();
    const entry = persisted[formId];
    if (!isFormDefinitionCacheEntryValid(entry)) {
        if (entry) {
            delete persisted[formId];
            writePersistedFormCache(persisted);
        }
        delete formDefinitionCache[formId];
        return null;
    }
    formDefinitionCache[formId] = entry;
    logWorkflow('form cache hit', {
        formId,
        source: 'localStorage',
        ageMs: Date.now() - entry.fetchedAt,
    });
    return entry.detail;
}
function setCachedFormDefinition(formId, detail) {
    const entry = { detail, fetchedAt: Date.now() };
    formDefinitionCache[formId] = entry;
    const persisted = { ...readPersistedFormCache(), [formId]: entry };
    writePersistedFormCache(persisted);
}
export function invalidateFormDefinitionCache(formId) {
    if (formId) {
        delete formDefinitionCache[formId];
        const persisted = readPersistedFormCache();
        delete persisted[formId];
        writePersistedFormCache(persisted);
        return;
    }
    formDefinitionCache = {};
    try {
        window.localStorage.removeItem(FORM_DEFINITION_CACHE_KEY);
    }
    catch {
        // localStorage can be unavailable in restricted browser contexts.
    }
}
function logWorkflow(step, data) {
    if (process.env.NODE_ENV === 'production')
        return;
    if (data !== undefined) {
        console.log(LOG_PREFIX, step, data);
    }
    else {
        console.log(LOG_PREFIX, step);
    }
}
async function postJson(path, body, options) {
    const url = getReferralsApiUrl(path);
    logWorkflow('request', { url, body });
    const headers = { 'Content-Type': 'application/json' };
    if (options?.auth) {
        headers.Authorization = await getValidAuthorizationHeader();
    }
    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body ?? {}),
    });
    const rawText = await response.text();
    logWorkflow('response', { status: response.status, rawText: rawText.slice(0, 500) });
    let payload;
    try {
        payload = JSON.parse(rawText);
    }
    catch {
        throw new Error(`Invalid JSON response (${response.status}): ${rawText.slice(0, 200)}`);
    }
    if (!response.ok || payload.error) {
        throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return payload;
}
export async function fetchPortalForms() {
    const payload = await postJson('/referrals/forms');
    if (!Array.isArray(payload.forms) || payload.forms.length === 0) {
        throw new Error('API response did not include a form catalog');
    }
    return payload.forms;
}
export async function fetchFormDefinition(formId) {
    const cached = getCachedFormDefinition(formId);
    if (cached) {
        return cached;
    }
    const payload = await postJson('/referrals/form-definition', { formId });
    const detail = payload.detail;
    if (!detail?.survey) {
        throw new Error('API response did not include a survey definition');
    }
    setCachedFormDefinition(formId, detail);
    return detail;
}
export async function submitFormReferral(formData, incidentPersonInvolved = [], submitter = {}, additionalForms = []) {
    logWorkflow('submitFormReferral formData keys', Object.keys(formData));
    logWorkflow('submitFormReferral additionalForms', additionalForms.map((f) => f.formId));
    const payload = await postJson('/referrals/submit', {
        formData,
        incidentPersonInvolved,
        userId: submitter.userId,
        firstName: submitter.firstName ?? '',
        lastName: submitter.lastName ?? '',
        email: submitter.email ?? '',
        additionalForms,
    }, { auth: true });
    if (!payload.incident || typeof payload.incident.id !== 'number') {
        throw new Error('Submit response missing incident');
    }
    return payload.incident;
}
/** Pull the newly-created incident id from the Kokomo create detail (with light fallbacks). */
export function extractIncidentId(incident) {
    const candidates = [
        incident.id,
        incident.incidentId,
        incident.caseId,
    ];
    for (const candidate of candidates) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return candidate;
        }
        if (typeof candidate === 'string' && candidate.trim() && !Number.isNaN(Number(candidate))) {
            return Number(candidate);
        }
    }
    return null;
}
export async function uploadReferralAttachment(request) {
    logWorkflow('uploadReferralAttachment', {
        caseId: request.caseId,
        fileName: request.fileName,
        contentLength: request.content.length,
    });
    const payload = await postJson('/referrals/attachment', request);
    return payload.result;
}
